import React, { FunctionComponent, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import '../../styles/ai-assistant.styles.scss';
import { updateAiAssistantContent } from '../../../../../store/action-creators/ai-tools';
import { DATA_QA } from '../../helpers/data-qa';
import { useTranslation } from 'react-i18next';
import { useAiForgeSeoTitle } from '../../helpers/ai-assistant-requests.helper';
import { IAiAssistantContent, IAiForgeResponseContent, IAiForgeTitleRequestContent } from '../../models/ai-assistant.models';
import { extractCleanBodyText, getAiToolsConfig, hasBodyText } from '../../helpers/ai-assistant.helper';
import AiAssistantOperationsContainer from '../subComponents/ai-assistant-operations-container';
import Article from '../../../../../models/article/Article';
import GenerateContentButton from './buttons/generate-button';
import AiAssistantSuggestionsContainer from './ai-assistant-suggestions-container';

type Properties = {
	aiAssistantContent: IAiAssistantContent;
	isRegenerating?: boolean;
	article: Article;
};

export const SeoTitleContainer: FunctionComponent<Properties> = ({ aiAssistantContent, isRegenerating, article }) => {
	const [t] = useTranslation();
	const dispatch = useDispatch();
	const featureConfig = getAiToolsConfig();
	const [toggleInfoPanel, setToggleInfoPanel] = useState(false);
	const [hasError, setHasError] = useState(false);
	const [selectedTitle, setSelectedTitle] = useState(
		aiAssistantContent && aiAssistantContent.seo_title && aiAssistantContent.seo_title.current_content
			? aiAssistantContent.seo_title.current_content
			: null,
	);

	const noBodyText = useMemo(() => {
		return hasBodyText(article);
	}, [article]);

	const hasSeoTitles = useMemo(() => {
		return (
			aiAssistantContent &&
			aiAssistantContent.seo_title &&
			Array.isArray(aiAssistantContent.seo_title.extracted_content) &&
			aiAssistantContent.seo_title.extracted_content.length > 0
		);
	}, [aiAssistantContent]);

	const { mutate: generateSeoTitle, isLoading: isExtracting } = useAiForgeSeoTitle(
		function onSuccess(data: IAiForgeResponseContent) {
			setHasError(false);
			dispatch(
				updateAiAssistantContent({
					seo_title: {
						current_content: null,
						extracted_content: data.data,
					},
				}),
			);
		},
		function onError(err: Error) {
			console.error('Error extracting title:', err);
			setHasError(true);
		},
	);

	const onInfoToggle = () => setToggleInfoPanel(!toggleInfoPanel);

	const onGenerate = () => {
		if (!article || !article.body) {
			return;
		}

		dispatch(
			updateAiAssistantContent({
				...aiAssistantContent,
				seo_title: {
					current_content: null,
					extracted_content: [],
				},
			}),
		);

		const bodyText = extractCleanBodyText(article.body);

		const requestPayload: IAiForgeTitleRequestContent = {
			body: bodyText,
			publication: featureConfig.publication,
			version: featureConfig.version,
			articleId: article.id ? article.id : null,
			regenerate: false,
		};

		generateSeoTitle(requestPayload);
	};

	return (
		<div data-qa={DATA_QA.AI_TOOLS_PANEL_SEO_TITLE} className='ai-tools-modal-body-content-section'>
			<div className='ai-tools-modal-body-content-header'>
				<div className='ai-tools-modal-body-content-header-title'>
					<span className='ai-tools-modal-body-content-title-label'>{t('seo_title')}</span>
					<div className='ai-tools-modal-body-content-info-icon' onClick={onInfoToggle} />
					{toggleInfoPanel && (
						<div className='ai-assistant-info-message'>
							<div className='ai-assistant-info-message-title'>
								{t('seo_title')}
								<div className='ai-assistant-info-close-icon' onClick={onInfoToggle} />
							</div>
							<div className='ai-assistant-info-message-description'>{t('ai_assistant_general_seo_title_description')}</div>
						</div>
					)}
				</div>
				<div className='ai-tools-modal-body-content-header-buttons-group'>
					<GenerateContentButton
						onClickAction={onGenerate}
						disabled={noBodyText}
						text={hasSeoTitles ? t('re_generate_all') : t('generate_all')}
					/>
				</div>
			</div>
			{isExtracting || !hasSeoTitles ? (
				<AiAssistantOperationsContainer
					field='seo_title'
					aiAssistantContent={aiAssistantContent}
					isExtracting={isExtracting}
					isRegenerating={!!isRegenerating}
					hasError={hasError}
				/>
			) : (
				<AiAssistantSuggestionsContainer field='seo_title' onSelect={setSelectedTitle} />
			)}
		</div>
	);
};
